<template>
  <RouterLink :to="`/product/${item.modelId}/${item.sku}`" class="productCard">
    <div class="productCard__image">
      <SanityImage
        :src="`${item.image}?w=160&h=160&fit=fill&crop=center&dpr=2&bg=F6F6F6&q=70`"
        :alt="item.title.de"
        loading="eager"
        width="80"
        height="80"
      />
    </div>

    <div class="productCard__content">
      <div class="productCard__header">
        <div class="productCard__title">{{ item.title.de }}</div>
        <CurrencyFormatter
          v-if="item.price.default[deviceStore.settings.country]?.price > 0"
          :value="item.price.default[deviceStore.settings.country]?.price"
          :currency="deviceStore.settings.currency"
          :class="
            item.price.default[deviceStore.settings.country]?.compareAt !==
            item.price.default[deviceStore.settings.country]?.price
              ? 'productCard__price--sale'
              : 'productCard__price'
          "
        />
      </div>

      <div class="productCard__details">
        <div class="productCard__info">
          <template v-if="item.colour"
            >{{ item.colour.name }} {{ item.finish ? capitalizeFirstLetter(item.finish) : ''
            }}<span v-if="wirecoreLabel" class="wirecore"> / {{ wirecoreLabel }}</span></template
          >
          <template v-if="item.size && item.type !== 'accessory'"
            ><br />{{ item.size.sizeGroup }} ({{ item.size.frameWidth }}mm)</template
          >
        </div>

        <div class="productCard__actions">
          <CurrencyFormatter
            v-if="
              item.price.default[deviceStore.settings.country]?.compareAt !==
              item.price.default[deviceStore.settings.country]?.price
            "
            :value="item.price.default[deviceStore.settings.country]?.compareAt"
            :currency="deviceStore.settings.currency"
            class="productCard__price--strikethrough"
          />
          <StockStatusIndicator
            v-if="item.stock && !item.doNotTrack"
            :status-color="getStockColour(item.stock[deviceStore.settings.warehouse])"
          />
          <wa-button
            v-if="addToCartEnabled"
            variant="neutral"
            appearance="outlined"
            size="s"
            class="quickAddToCart"
            :loading="addToCartLoading"
            @click.prevent="addAccessoryToCart(item)"
            ><PhShoppingBag :size="18"
          /></wa-button>
        </div>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import SanityImage from '@/components/shared/SanityImage.vue'
import { useCart } from '@/composables/useCart.js'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomerStore } from '@/stores/customer'
import { useDeviceStore } from '@/stores/device'
import { useI18n } from 'vue-i18n'

import CurrencyFormatter from '@/components/shared/CurrencyFormatter/CurrencyFormatter.vue'
import StockStatusIndicator from '@/components/shared/StockStatusIndicator/StockStatusIndicator.vue'
import { getStockColour } from '@/utils/stock.js'
import { getWirecoreLabel } from '@/utils/product.js'
import { PhShoppingBag } from '@phosphor-icons/vue'
import '@awesome.me/webawesome/dist/components/button/button.js'
import '@awesome.me/webawesome/dist/components/format-number/format-number.js'
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'

const deviceStore = useDeviceStore()
const customerStore = useCustomerStore()
const { createCart, addLineItem, cartUpdate } = useCart()
const addToCartLoading = ref(false)

const notification = useNotification()
const { t } = useI18n()

const props = defineProps({
  item: {
    type: Object,
    default: () => ({})
  },
  index: {
    type: Number,
    default: 0
  }
})

function capitalizeFirstLetter(string = '') {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
}

const wirecoreLabel = computed(() => getWirecoreLabel(props.item))

const addToCartEnabled = computed(() => {
  if (
    !props.item.stock ||
    !props.item.stock[deviceStore.settings.warehouse] ||
    props.item.stock[deviceStore.settings.warehouse]?.status !== 1
  ) {
    return false
  }

  if (
    props.item.type === 'accessory' &&
    ['the-cleaning-cloth', 'the-cleaning-kit'].includes(props.item.slug)
  ) {
    return true
  }

  if (props.item.type !== 'accessory' || props.item.sku === 'giftcard') {
    return false
  }

  if (props.item.productStatus === 'COMING_SOON') {
    return false
  }

  if (props.item.doNotTrack) {
    return true
  }

  return true
})

async function addAccessoryToCart(item) {
  addToCartLoading.value = true
  if (!customerStore.customer?.currentCart) {
    await createCart()
  }

  const mainResponse = await addLineItem({
    sku_code: item.sku,
    quantity: 1,
    metadata: {
      type: 'accessory',
      ...(!props.item.doNotTrack && {
        lead_time_days: item.stock[deviceStore.settings.warehouse].leadTimeDays
      }),
      origin_stock_location: deviceStore.settings.warehouse,
      immediate_handover: true
    }
  })

  if (mainResponse) {
    await cartUpdate()
    addToCartLoading.value = false

    notification.showNotification({
      message: `${props.item.title[deviceStore.settings.language]} ${t(
        'configurator.infoMessages.productWasAddedToCart'
      )}`
    })

    return
  }

  addToCartLoading.value = false
}
</script>

<style scoped>
.productCard {
  --spacing: 12px;
  display: flex;
  gap: 12px;
  padding: 12px;
  border: 1px solid var(--wa-color-neutral-80);
  border-radius: 4px;
  text-decoration: none;
  color: var(--color--black);
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--wa-color-neutral-70);
  }

  &__image {
    flex-shrink: 0;
    width: 80px;
    height: 80px;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      border-radius: 2px;
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-width: 0;
  }

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }

  &__title {
    text-transform: uppercase;
    letter-spacing: 0.03em;
    font-weight: var(--font--bold);
    font-size: 14px;
    line-height: 1.4;
  }

  &__price {
    font-weight: var(--wa-font-weight-semibold);
    white-space: nowrap;

    &--sale {
      color: red;
      font-weight: var(--wa-font-weight-semibold);
      white-space: nowrap;
    }

    &--strikethrough {
      text-decoration: line-through;
      color: var(--wa-color-neutral-50);
      font-size: 12px;
    }
  }

  &__details {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 8px;
  }

  &__info {
    font-size: 13px;
    color: var(--wa-color-neutral-40);
    line-height: 1.4;
  }

  &__actions {
    display: flex;
    gap: 6px;
    align-items: center;
  }
}

wa-button.quickAddToCart {
  &[variant='neutral'][appearance='outlined'] {
    &::part(base) {
      height: 32px;
      width: 32px;
      padding: 0;
      border: 1px solid var(--wa-color-neutral-70);
      border-radius: var(--wa-form-control-border-radius);
    }
    &::part(label) {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0;
      color: var(--color--black);
    }
  }
}
</style>
